import {
  assessFraming,
  framingSceneAt,
  modelSlugOf,
  modelStatesAt,
  sceneStateAt,
  type Bounds,
  type Lesson,
  type ModelCatalogue,
  type ObjectState,
  type WorldCatalogue,
} from '@vrlearning/lesson-schema';

/**
 * The lesson's picture (ADR-098): which step's settled scene a lesson card shows, by default.
 *
 * "The most interesting moment" is read as the step where the lesson's main model covers the most
 * of the picture, seen from where the pupil stands at that step -- the engine's own framing walk
 * (`assessFraming`: the same eyes and turn the headset gives each step) and the scene as the step
 * ends (`framingSceneAt`, the models where the steps leave them). A quiz or a prediction is never
 * chosen: its lectern is the moment, not the scene. An Onderzoeken step (hotspots) or a step whose
 * models do something counts a quarter more. Pure and deterministic: the same lesson gives the
 * same step, the earliest of equals.
 */

/** The picture's frame: 16:9, 60° high -- narrower than the desktop preview's 85°, as a photo is. */
export const PICTURE_FRAME = { aspect: 16 / 9, fov: 60 } as const;

/** What an Onderzoeken step or a step with model entries counts for, beside another. */
export const PICTURE_PREFERRED = 1.25;

export type PictureStepScore = {
  readonly step: string;
  readonly cover: number;
  readonly score: number;
};

export type PictureStepChoice = {
  /** The step to draw. */
  readonly step: string;
  /** The object the choice is about; undefined in a lesson without objects. */
  readonly main: string | undefined;
  /** Every step that may be drawn, with its share of the picture the main model covers. */
  readonly scores: readonly PictureStepScore[];
};

type Vec3 = readonly [number, number, number];

/** A step a picture may be of: anything but a question. */
export const mayPicture = (step: Lesson['steps'][number]): boolean =>
  step.quiz === undefined && step.predict === undefined;

/**
 * The lesson's main object: of its library models (else of all its objects), the one its steps name
 * most -- as what they look at, focus on, point a hotspot at or give an entry; the first declared of
 * equals, and the first declared when none is named.
 */
export function mainObjectOf(lesson: Lesson): string | undefined {
  const objects = Object.entries(lesson.objects ?? {});
  const models = objects.filter(([, object]) => modelSlugOf(object.kind) !== undefined);
  const candidates = (models.length > 0 ? models : objects).map(([id]) => id);
  if (candidates.length === 0) return undefined;
  const counts = new Map(candidates.map((id) => [id, 0]));
  for (const step of lesson.steps) {
    const named = new Set<string>();
    const view = step.view;
    if (view && 'look' in view && typeof view.look === 'string') named.add(view.look);
    if (step.focus !== undefined) named.add(step.focus);
    for (const hotspot of step.hotspots ?? []) if (hotspot.object) named.add(hotspot.object);
    for (const entry of step.models ?? []) named.add(entry.object);
    for (const id of named) if (counts.has(id)) counts.set(id, counts.get(id)! + 1);
  }
  let best = candidates[0]!;
  for (const id of candidates) if (counts.get(id)! > counts.get(best)!) best = id;
  return best;
}

/** Points on a subject's box, corners and edge middles, or round a round thing. */
function samples(bounds: Bounds): Vec3[] {
  if (bounds.round) {
    const { centre, radius, height } = bounds.round;
    const points: Vec3[] = [];
    for (let k = 0; k < 16; k++) {
      const t = (2 * Math.PI * k) / 16;
      points.push([centre[0] + radius * Math.cos(t), centre[1], centre[2] + radius * Math.sin(t)]);
    }
    points.push(
      [centre[0], centre[1] + height, centre[2]],
      [centre[0], centre[1] - height, centre[2]],
    );
    return points;
  }
  const { min, max } = bounds.box;
  const xs = [min[0], (min[0] + max[0]) / 2, max[0]];
  const ys = [min[1], (min[1] + max[1]) / 2, max[1]];
  const zs = [min[2], (min[2] + max[2]) / 2, max[2]];
  const points: Vec3[] = [];
  for (const x of xs) for (const y of ys) for (const z of zs) points.push([x, y, z]);
  return points;
}

/**
 * How well the box round `bounds` fills a 16:9 picture (`PICTURE_FRAME`), drawn from `eye` with the
 * rig turned `yaw` (yawTowards) and the camera tilted `pitch` (up positive), as Stage.placeRig turns
 * and tilts the desktop camera: the share of the picture it covers, times the share of it that is in
 * the picture -- so a model three times the frame (a crop of a leg) counts a third of one that just
 * fills it. 0 when it is behind, and when the eyes are inside it (a picture from inside the geometry).
 */
export function pictureCover(bounds: Bounds, eye: Vec3, yaw: number, pitch: number): number {
  const { min, max } = bounds.box;
  if ([0, 1, 2].every((i) => eye[i]! >= min[i]! && eye[i]! <= max[i]!)) return 0;
  const tanV = Math.tan((PICTURE_FRAME.fov * Math.PI) / 360);
  const tanH = tanV * PICTURE_FRAME.aspect;
  const [cy, sy] = [Math.cos(yaw), Math.sin(yaw)];
  const [cp, sp] = [Math.cos(pitch), Math.sin(pitch)];
  let left = Infinity;
  let right = -Infinity;
  let bottom = Infinity;
  let top = -Infinity;
  let seen = false;
  for (const p of samples(bounds)) {
    const dx = p[0] - eye[0];
    const dy = p[1] - eye[1];
    const dz = p[2] - eye[2];
    // Into the rig's frame (turned back by yaw), then the camera's (tilted back by pitch).
    const lx = dx * cy - dz * sy;
    const lz = dx * sy + dz * cy;
    const y = dy * cp + lz * sp;
    const depth = -(-dy * sp + lz * cp);
    // A point behind the eyes reaches out of the picture on its own side.
    const nx = depth <= 0.05 ? Math.sign(lx) * Infinity || 0 : lx / (depth * tanH);
    const ny = depth <= 0.05 ? Math.sign(y) * Infinity || 0 : y / (depth * tanV);
    if (depth > 0.05) seen = true;
    left = Math.min(left, nx);
    right = Math.max(right, nx);
    bottom = Math.min(bottom, ny);
    top = Math.max(top, ny);
  }
  if (!seen) return 0;
  const width = Math.max(0, Math.min(right, 1) - Math.max(left, -1));
  const height = Math.max(0, Math.min(top, 1) - Math.max(bottom, -1));
  const covered = (width * height) / 4;
  // The whole of it as drawn, in and out of the picture (the picture is 2 × 2 here).
  const whole = ((right - left) * (top - bottom)) / 4;
  if (covered === 0 || !Number.isFinite(whole)) return 0;
  return covered * Math.min(1, covered / whole);
}

/**
 * The step a lesson's picture is of (see above), or the one `asked` names when it may be drawn. A
 * lesson whose steps are all questions, or whose main object is never in view: its first step that
 * is not a question, else its first.
 */
export function pictureStep(
  lesson: Lesson,
  catalogue: ModelCatalogue = {},
  worlds?: WorldCatalogue,
  asked?: string,
): PictureStepChoice {
  const main = mainObjectOf(lesson);
  const statesAt: (index: number) => ReadonlyMap<string, ObjectState> =
    Object.keys(catalogue).length > 0
      ? modelStatesAt(lesson, catalogue, undefined, worlds)
      : (index) => sceneStateAt(lesson, index);
  const scores: PictureStepScore[] = [];
  if (lesson.mode === 'vr' && main !== undefined) {
    const framing = assessFraming(
      lesson,
      1.6,
      { statesAt, ...(worlds ? { worlds } : {}) },
      catalogue,
    );
    let pitch = 0;
    lesson.steps.forEach((step, index) => {
      const at = framing.steps[index];
      if (!at) return;
      const scene = framingSceneAt(lesson, index, statesAt, catalogue, worlds);
      const bounds = scene.bounds.get(main);
      // The desktop camera tilts to a step's view (placeRig) and keeps its tilt without one.
      if (step.view && bounds) {
        const centre = [
          (bounds.box.min[0] + bounds.box.max[0]) / 2,
          (bounds.box.min[1] + bounds.box.max[1]) / 2,
          (bounds.box.min[2] + bounds.box.max[2]) / 2,
        ] as const;
        pitch = Math.atan2(
          centre[1] - at.eye[1],
          Math.hypot(centre[0] - at.eye[0], centre[2] - at.eye[2]),
        );
      }
      if (!mayPicture(step)) return;
      const cover = bounds ? pictureCover(bounds, at.eye, at.yaw, pitch) : 0;
      const preferred = (step.hotspots?.length ?? 0) > 0 || (step.models?.length ?? 0) > 0;
      scores.push({ step: step.id, cover, score: cover * (preferred ? PICTURE_PREFERRED : 1) });
    });
  }
  if (asked !== undefined && lesson.steps.some((step) => step.id === asked && mayPicture(step))) {
    return { step: asked, main, scores };
  }
  let best: PictureStepScore | undefined;
  for (const score of scores) if (score.score > (best?.score ?? 0)) best = score;
  const fallback = lesson.steps.find(mayPicture) ?? lesson.steps[0];
  return { step: best?.step ?? fallback.id, main, scores };
}

/**
 * How the picture page reaches step `target` from step `from` (main.ts, bootLessonPicture): the steps
 * to go to in turn (the last is `target`; none when it is there already), and whether `target`'s
 * changes are then played out rather than put at their calm end. By way of a step that does not lead
 * into it, so the player snaps it to its calm end; a step without a view by way of the last step with
 * one, so the pupil stands where they would in class; a lesson too short for that goes to the step
 * before and plays the step's changes.
 */
export function pictureRoute(
  steps: readonly { readonly view?: unknown }[],
  from: number,
  target: number,
): { hops: number[]; play: boolean } {
  let anchor = -1;
  for (let index = 0; index < target; index++) if (steps[index]?.view !== undefined) anchor = index;
  const candidates =
    steps[target]?.view !== undefined
      ? [from, target + 1, target - 2]
      : anchor >= 0
        ? [anchor]
        : [from < target ? from : -1, 0];
  const via = candidates.find(
    (index) => index >= 0 && index < steps.length && index !== target && index !== target - 1,
  );
  if (via !== undefined) return { hops: via === from ? [target] : [via, target], play: false };
  const hops: number[] = [];
  if (target > 0 && from !== target - 1 && from !== target) hops.push(target - 1);
  if ((hops.at(-1) ?? from) !== target) hops.push(target);
  return { hops, play: true };
}
